import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { WeaveCopilotV1 } from '@frontend/api-weave-copilot';
import { getUser } from '@frontend/auth-helpers';
import { useComponentEngagementShallowStore } from '@frontend/high-value-adoption';
import { useAppScopeStore } from '@frontend/scope';
import { WEAVE_COPILOT_HOTKEY } from '../constant';
import { ChatbotAssistantContextProps } from '../provider/chatbot-assistant.provider';
import { useCopilotChatSessionStore } from '../store/copilot-chat-session-store';
import { AIChatbotResponse, MessageFeedback, WeaveCopilotSource } from '../types';

const COPILOT_TRIGGER_INTERACTION_KEY = 'weave-copilot-trigger-interaction';

export const useChatbotAssistantConfig = ({
  scopeLocationId,
  sessionId,
  source = 'top-bar',
}: {
  scopeLocationId?: string;
  sessionId?: string;
  source?: WeaveCopilotSource;
} = {}): ChatbotAssistantContextProps => {
  const {
    addMessage: addMessageInSession,
    initSession,
    updateFeedback: updateFeedbackInSession,
  } = useCopilotChatSessionStore();

  const { getEngagementData, trackInteraction } = useComponentEngagementShallowStore(
    'trackInteraction',
    'getEngagementData'
  );

  const [currentSessionId, setCurrentSessionId] = useState<string>(sessionId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [messages, setMessages] = useState<AIChatbotResponse[]>([]);
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = scopeLocationId || selectedLocationIds[0];
  const username = getUser()?.username || 'unknown';

  const chatbotPopoutWindowRef = useRef<Window | null>(null);
  const popoutWindowCloseIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { interactionCount } = getEngagementData({
    componentId: COPILOT_TRIGGER_INTERACTION_KEY,
    source: username,
  });

  const { mutate: sendMessageMutate, isLoading: isSending } = WeaveCopilotV1.Mutations.useSendMessageMutation({
    options: {
      onSuccess: (response) => {
        addMessageInSession(currentSessionId, {
          role: 'assistant',
          message: { content: response.content?.text?.join('\n') ?? '' },
          id: response.id || Date.now().toString(),
          timestamp: new Date().toISOString(),
          type: 'text',
        });
      },
      onError: (error) => {
        if (error.status === 500) {
          addMessageInSession(currentSessionId, {
            role: 'assistant',
            message: { content: 'Sorry, something went wrong on our end. Please retry.' },
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            type: 'error',
          });
        } else if (error.status === 429) {
          addMessageInSession(currentSessionId, {
            role: 'assistant',
            message: { content: 'You have reached the message limit. Please wait a moment before trying again.' },
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            type: 'error',
          });
        }
        console.error('Error sending message:', error);
      },
    },
  });

  const addMessage = useCallback(
    (chatbotResponse: AIChatbotResponse) => {
      !!currentSessionId && addMessageInSession(currentSessionId, chatbotResponse);
    },
    [currentSessionId, addMessageInSession]
  );

  const updateFeedback = useCallback(
    (messageId: string, feedback: MessageFeedback) => {
      !!currentSessionId &&
        updateFeedbackInSession({
          sessionId: currentSessionId,
          messageId,
          feedback,
        });
    },
    [currentSessionId, updateFeedbackInSession]
  );

  const sendMessage = useCallback(
    (message: string) => {
      addMessage({
        role: 'user',
        message: { content: message },
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: 'text',
      });

      sendMessageMutate({
        message: {
          text: message,
        },
        locationId,
      });
    },
    [addMessage, locationId, sendMessageMutate]
  );

  const toggleChatbotOpen = useCallback(() => {
    if (isPopoutOpen && chatbotPopoutWindowRef.current && !chatbotPopoutWindowRef.current.closed) {
      chatbotPopoutWindowRef.current.focus();
      return;
    }
    trackInteraction({
      componentId: COPILOT_TRIGGER_INTERACTION_KEY,
      source: username,
    });
    setIsPopoutOpen(false);
    setIsOpen((state) => !state);
  }, [isPopoutOpen, trackInteraction, username]);

  // Handle keyboard shortcuts
  useHotkeys(WEAVE_COPILOT_HOTKEY.shortcut, toggleChatbotOpen);

  const openPopoutWindow = useCallback(() => {
    if (chatbotPopoutWindowRef.current && !chatbotPopoutWindowRef.current.closed) {
      chatbotPopoutWindowRef.current.focus();
      return;
    }

    chatbotPopoutWindowRef.current = window.open(
      `/weave-copilot?popout=true&locationId=${locationId}&sessionId=${currentSessionId}`,
      'Weave Copilot',
      'width=400,height=600,left=100,top=100,resizable=yes,scrollbars=yes,status=no,menubar=no,toolbar=no'
    );

    if (!chatbotPopoutWindowRef.current) {
      console.warn('Failed to open chatbot popout window.');
      return;
    }

    setIsPopoutOpen(true);
  }, [locationId, currentSessionId]);

  useEffect(() => {
    if (!currentSessionId) return;

    // Immediately sync messages from the store
    setMessages(useCopilotChatSessionStore.getState().sessionsMap[currentSessionId]?.messages ?? []);

    // Subscribe to future changes
    const unsubscribe = useCopilotChatSessionStore.subscribe(
      (state) => state.sessionsMap[currentSessionId],
      (sessionMessages) => setMessages(sessionMessages ? sessionMessages.messages : [])
    );

    return () => {
      unsubscribe();
    };
  }, [currentSessionId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!currentSessionId) {
      const newSessionId = initSession();
      setCurrentSessionId(newSessionId);
    }
  }, [currentSessionId, initSession, isOpen]);

  useEffect(() => {
    if (isPopoutOpen && chatbotPopoutWindowRef.current) {
      popoutWindowCloseIntervalRef.current = setInterval(() => {
        if (chatbotPopoutWindowRef.current?.closed) {
          console.log('Chatbot popout window closed detected by interval.');
          chatbotPopoutWindowRef.current = null;
          setIsPopoutOpen(false);
          setIsOpen(false);

          // Rehydrate the store to sync any messages added in the popout back to the main window
          useCopilotChatSessionStore.persist.rehydrate();
        }
      }, 500);
    } else if (popoutWindowCloseIntervalRef.current) {
      clearInterval(popoutWindowCloseIntervalRef.current);
    }
  }, [isPopoutOpen]);

  useEffect(() => {
    return () => {
      !!popoutWindowCloseIntervalRef.current && clearInterval(popoutWindowCloseIntervalRef.current);
    };
  }, []);

  return {
    messages,
    isLoading,
    isSending,
    isOpen,
    position,
    locationId,
    isPopoutOpen,
    source,
    isFirstTimeInteracted: interactionCount === 1,
    addMessage,
    updateFeedback,
    openPopoutWindow,
    sendMessage,
    setIsLoading,
    toggleChatbotOpen,
    setPosition,
    setIsPopoutOpen,
  };
};
